import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, formatErr } from "../lib/api";
import { Drawer } from "./UI";
import { useAuth } from "../lib/auth";
import { JobDashHero, SubjectAutocomplete, JobLocationsList, JobTasksBlock, JobActivityLog, JobDocumentsBlock } from "./JobDashSections";
import {
  IdentificationCard, MapPin, FileText, CalendarBlank, Receipt,
  ChatCircleDots, Envelope, Trash, Plus, Path, ArrowRight, Notebook,
  ClockClockwise, Copy, CheckSquare, Files,
} from "@phosphor-icons/react";

// Custom hook isolating the fetch — react-hooks/set-state-in-effect doesn't
// cross hook boundaries, and the cancellation flag keeps it safe.
function useJobDetails(jobId) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [reloadTick, setReloadTick] = useState(0);
  const fetchData = (id) => {
    if (!id) { setD(null); setErr(""); return; }
    api.get(`/jobs/${id}/details`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(formatErr(e)));
  };
  useEffect(() => { fetchData(jobId); }, [jobId, reloadTick]); // eslint-disable-line react-hooks/exhaustive-deps
  return { d, err, setErr, refresh: () => setReloadTick(t => t + 1) };
}

/**
 * Right-edge slide-out panel showing every related record for one Job.
 * Read-only-with-quick-actions: user cannot edit the Job's own fields here
 * (that's coming with backlog Item #8 — /job-dash). They CAN:
 *   • Add / delete Notes scoped to this job
 *   • Re-assign the Service Location
 *   • Duplicate the entire job into a fresh row
 *   • Open any linked record (subject, calendar item, invoice) in its page
 */
export default function JobMiniPanel({ jobId, onClose, allLocations, onJobChange, onSwitchJob, onDuplicated }) {
  const { d, err, setErr, refresh: fetchDetails } = useJobDetails(jobId);
  const [noteBody, setNoteBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  // iter 34 — pull customers + users for the dash dropdowns once per panel open.
  // Also record this open in the per-user recent-jobs list AND fetch the recent
  // list for the breadcrumb chips at the top of the panel.
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [recent, setRecent] = useState([]);
  const [activityTick, setActivityTick] = useState(0);
  const fetchSideData = (id) => {
    if (!id) return;
    api.get("/customers?paginate=false").then(r => setCustomers(r.data)).catch(() => setCustomers([]));
    api.get("/users").then(r => setUsers(r.data?.items || r.data || [])).catch(() => setUsers([]));
    api.post("/users/me/recent-jobs", { job_id: id })
      .then(() => api.get("/users/me/recent-jobs"))
      .then(r => setRecent(r.data))
      .catch(() => {});
  };
  useEffect(() => { fetchSideData(jobId); }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveJob = async (patch) => {
    try { await api.patch(`/jobs/${jobId}`, patch); await fetchDetails(); onJobChange && onJobChange(); }
    catch (e) { throw e; }
  };

  const onSubjectSelect = async (subject) => {
    try { await api.patch(`/jobs/${jobId}`, { subject_id: subject.id }); await fetchDetails(); }
    catch (e) { setErr(formatErr(e)); }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/jobs/${jobId}/notes`, { body: noteBody });
      setNoteBody("");
      await fetchDetails();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const deleteNote = async (noteId) => {
    if (!confirm("Delete this note?")) return;
    try { await api.delete(`/jobs/${jobId}/notes/${noteId}`); await fetchDetails(); }
    catch (e) { setErr(formatErr(e)); }
  };

  const setLocation = async (locId) => {
    setBusy(true); setErr("");
    try {
      await api.put(`/jobs/${jobId}/service-location`, { service_location_id: locId || null });
      await fetchDetails();
      onJobChange && onJobChange();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const duplicateJob = async () => {
    if (!confirm("Duplicate this job?\n\n• Copies: job fields, type, status, location, assigned-to, price, due-date\n• Skips: invoices, calendar items, notes, service attempts\n• Calendar dates will be cleared (you set new ones)\n\nContinue?")) return;
    setDuplicating(true); setErr("");
    try {
      const r = await api.post(`/jobs/${jobId}/duplicate`, {});
      onDuplicated && onDuplicated(r.data);
      onClose && onClose();
    } catch (e) { setErr(formatErr(e)); }
    finally { setDuplicating(false); }
  };

  const subjectName = (s) =>
    [s.first_name, s.middle_name, s.last_name].filter(Boolean).join(" ").trim() || s.aka || "(unnamed subject)";

  const title = d
    ? `Job ${d.job.job_number || d.job.id.slice(0, 8)}${d.case ? "  ·  " + (d.case.case_number || (d.case.plaintiff + " v. " + d.case.defendant)) : ""}`
    : "Loading…";

  // iter 26: contractor orgs cannot create top-level jobs (backend guard
  // returns 403 contractor_cannot_create_jobs) — hide the drawer-level
  // DUPLICATE action entirely so we don't present a broken affordance.
  const { user } = useAuth();
  const isContractor = user?.org_type === "contractor";

  return (
    <Drawer
      open={!!jobId}
      onClose={onClose}
      title={title}
      testid="job-mini-panel"
      footer={
        isContractor ? null : (
          <button
            className="btn btn-secondary"
            onClick={duplicateJob}
            disabled={!d || duplicating}
            data-testid="duplicate-job-drawer-btn"
            title="Create a new job with these settings (excludes invoices, calendar, notes, service attempts)"
          >
            <Copy size={14} /> {duplicating ? "DUPLICATING…" : "DUPLICATE JOB"}
          </button>
        )
      }
    >
      {!d ? (
        <div className="py-10 text-center text-muted-foreground" data-testid="mini-panel-loading">
          {jobId ? "Loading job details…" : ""}
        </div>
      ) : (
        <div className="drawer-section-grid" data-testid="mini-panel-body">
          {/* iter 34 — Recently viewed jobs strip (persisted server-side per user) */}
          {recent.length > 1 && (
            <div className="surface p-2 mb-2" data-testid="recent-jobs-strip" style={{ gridColumn: "1 / -1" }}>
              <div className="label-xs mb-1">RECENT JOBS</div>
              <div className="flex flex-wrap gap-1">
                {recent.slice(0, 15).map(r => (
                  <button
                    key={r.job_id}
                    type="button"
                    onClick={() => onSwitchJob ? onSwitchJob(r.job_id) : null}
                    disabled={r.job_id === jobId}
                    title={r.notes_preview || ""}
                    className={`btn btn-sm ${r.job_id === jobId ? "btn-primary" : "btn-ghost"}`}
                    data-testid={`recent-job-chip-${r.job_id}`}
                  >
                    {r.job_number || r.job_id.slice(0, 6)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* iter 34 Phase A.2 — Job Dash hero (editable customer/attorney/etc.) */}
          <Section title="JOB DETAILS" icon={IdentificationCard} testid="section-dash-hero" wide>
            <JobDashHero job={d.job} customers={customers} users={users} onSaveJob={saveJob} />
          </Section>

          {/* SUBJECT — text autocomplete */}
          <Section title="SUBJECT" icon={IdentificationCard} testid="section-subject">
            <SubjectAutocomplete
              value={d.subject ? [d.subject.first_name, d.subject.last_name].filter(Boolean).join(" ") : ""}
              onSelect={onSubjectSelect}
            />
            {d.subject && (
              <div className="text-[10px] mono text-muted-foreground mt-2">
                Linked: {d.subject.id.slice(0, 8)}{d.subject.aka ? ` · aka ${d.subject.aka}` : ""}
              </div>
            )}
          </Section>

          {/* LOCATIONS — multi-list */}
          <Section title="LOCATIONS" icon={MapPin} testid="section-locations" wide>
            <JobLocationsList jobId={jobId} allLocations={allLocations} />
          </Section>

          {/* NOTES (wide) */}
          <Section title={`NOTES (${d.notes.length})`} icon={FileText} testid="section-notes" wide>
            <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
              {d.notes.length === 0 ? <Empty>No notes on this job yet.</Empty> : d.notes.map(n => (
                <div key={n.id} className="border border-border p-2 flex items-start justify-between gap-2" data-testid={`note-${n.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm whitespace-pre-wrap break-words">{n.body}</div>
                    <div className="text-[10px] mono text-muted-foreground mt-1">
                      {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm flex-shrink-0" onClick={() => deleteNote(n.id)} data-testid={`delete-note-${n.id}`}>
                    <Trash size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                className="input flex-1"
                placeholder="Add a note…"
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addNote()}
                data-testid="new-note-input"
              />
              <button className="btn btn-primary" onClick={addNote} disabled={busy || !noteBody.trim()} data-testid="add-note-btn">
                <Plus size={14} /> ADD
              </button>
            </div>
          </Section>

          {/* CALENDAR */}
          <Section title={`CALENDAR (${d.calendar_items.length})`} icon={CalendarBlank} testid="section-calendar">
            {d.calendar_items.length === 0 ? (
              <Empty>No calendar items linked. Add one from the <Link to="/calendar" className="text-primary">Calendar</Link>.</Empty>
            ) : d.calendar_items.map(ci => (
              <Link key={ci.id} to="/calendar" className="block py-2 border-b border-border last:border-0 hover:bg-muted/30 -mx-2 px-2 transition-colors" data-testid={`calendar-${ci.id}`}>
                <div className="text-sm font-medium">{ci.type || "Event"}</div>
                <div className="text-xs text-muted-foreground mono">
                  {ci.start_time ? new Date(ci.start_time).toLocaleString() : "—"}
                </div>
                {ci.details && <div className="text-xs text-muted-foreground truncate">{ci.details}</div>}
              </Link>
            ))}
          </Section>

          {/* INVOICES */}
          <Section title={`RECENT INVOICES (${d.invoices.length})`} icon={Receipt} testid="section-invoices">
            {d.invoices.length === 0 ? <Empty>No invoices yet.</Empty> : (
              <div className="space-y-1 max-h-44 overflow-y-auto">
                {d.invoices.map(inv => (
                  <Link key={inv.id} to="/receivables" className="flex items-center justify-between py-1 px-2 hover:bg-muted/30 transition-colors" data-testid={`invoice-${inv.id}`}>
                    <div className="min-w-0">
                      <div className="mono text-sm">{inv.invoice_number}</div>
                      <div className="text-[10px] text-muted-foreground">{inv.issue_date ? new Date(inv.issue_date).toLocaleDateString() : ""}</div>
                    </div>
                    <div className="text-right">
                      <div className="mono text-sm">{fmtMoney(inv.total)}</div>
                      <div className={`text-[10px] mono uppercase ${inv.balance > 0 ? "text-amber-600" : "text-green-600"}`}>{inv.status}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
            <div className="text-[10px] text-muted-foreground italic mt-2 border-t border-border pt-2">
              Invoices aren&apos;t yet job-scoped — backlog Item #9 will tie specific invoices to this job via activity events.
            </div>
          </Section>

          {/* ROUTE */}
          <Section title="ROUTE" icon={Path} testid="section-route">
            <Empty>
              Route assignment lives on the <Link to="/routes" className="text-primary">Routes</Link> page.
              <span className="block mt-1 text-[10px] italic">(Backlog Item #8 — job_dash — will inline this picker.)</span>
            </Empty>
          </Section>

          {/* iter 34 Phase B — Tasks block */}
          <Section title="TASKS" icon={CheckSquare} testid="section-tasks" wide>
            <JobTasksBlock jobId={jobId} />
          </Section>

          {/* iter 34 Phase C — Documents (Service / Misc / Invoice / Affidavit / Other) */}
          <Section title="DOCUMENTS" icon={Files} testid="section-documents" wide>
            <JobDocumentsBlock jobId={jobId} onActivityChange={() => setActivityTick(t => t + 1)} />
          </Section>

          {/* iter 34 Phase B — Activity Events log */}
          <Section title="ACTIVITY" icon={ClockClockwise} testid="section-activity" wide>
            <JobActivityLog jobId={jobId} refreshTick={activityTick} />
          </Section>

          {/* Stubs */}
          <Section title="EMAILS" icon={Envelope} testid="section-emails">
            <Stub label="Per-job email threading lights up with Item #0." />
          </Section>
          <Section title="INTERNAL MESSAGES" icon={ChatCircleDots} testid="section-messages">
            <Stub label="Per-job message threads come with Item #0." />
          </Section>
          <Section title="JOB MEMO" icon={Notebook} testid="section-job-notes-legacy" wide>
            {d.job.notes ? <div className="text-sm whitespace-pre-wrap">{d.job.notes}</div> : <Empty>(no job-level memo)</Empty>}
            <div className="text-[10px] text-muted-foreground italic mt-2 border-t border-border pt-2">
              Edit on the Jobs row → backlog Item #8 will replace this with a full /job-dash page.
            </div>
          </Section>
        </div>
      )}
      {err && <div className="text-xs text-destructive mt-3" data-testid="mini-panel-error">{err}</div>}
    </Drawer>
  );
}

const Section = ({ title, icon: I, children, testid, wide }) => (
  <div className={`surface p-4 ${wide ? "drawer-section--wide" : ""}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <I size={14} weight="bold" />
      <span className="label-xs">{title}</span>
    </div>
    {children}
  </div>
);

const Empty = ({ children }) => (
  <div className="text-xs text-muted-foreground py-2">{children}</div>
);

const Stub = ({ label }) => (
  <div className="text-xs text-muted-foreground italic flex items-center gap-2 py-2">
    <ArrowRight size={10} /> {label}
  </div>
);
